/**
 * Кто может входить в этот каталог.
 *
 * Вход по номеру без пароля опасен ровно тем, что окно открыто всегда.
 * Здесь его закрывают: у каждого человека счётчик оставшихся входов, и
 * владелец каталога закрывает замок, когда все свои устройства уже вошли.
 * После этого знание номера бесполезно.
 *
 * Кнопок две, и это не избыточность. Замок запрещает НОВЫЕ входы; ключи,
 * выданные раньше, продолжают работать. Потерянный телефон закрывается
 * второй кнопкой — «Отключить устройства».
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../sync';
import { formatPhone, normalizePhone, looksLikePhone, plural } from '@core';

interface Person {
  id: string;
  name: string;
  phone: string | null;
  role: string;
  login_quota: number;
  devices: number;
  last_seen: string | null;
}

export default function Access({ onBack }: { onBack: () => void }) {
  const [people, setPeople] = useState<Person[]>([]);
  const [meId, setMeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', logins: 1 });
  const [problem, setProblem] = useState('');

  async function load() {
    const res = await apiFetch('/api/users');
    if (res.status === 403) { setDenied(true); setLoading(false); return; }
    if (res.ok) {
      const d = await res.json();
      setPeople(d.users);
      setMeId(d.me);
    }
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  function flash(text: string) {
    setToast(text);
    setTimeout(() => setToast(''), 2800);
  }

  async function act(id: string, path: string, body?: unknown, done?: string) {
    setBusy(id + path);
    const res = await apiFetch(`/api/users/${id}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
    setBusy('');
    if (res.ok && done) flash(done);
    await load();
  }

  async function add() {
    setProblem('');
    if (!form.name.trim()) return setProblem('Впишите имя');
    if (!looksLikePhone(form.phone)) return setProblem('Номер введён не полностью');

    setBusy('new');
    const res = await apiFetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: form.name.trim(), phone: normalizePhone(form.phone), logins: form.logins }),
    });
    setBusy('');
    const d = await res.json().catch(() => ({}));
    if (!res.ok) return setProblem(d?.error ?? 'Не получилось добавить');

    setAdding(false);
    setForm({ name: '', phone: '', logins: 1 });
    flash('Добавлен. Пусть введёт свой номер на gemsnote.app');
    await load();
  }

  if (loading) {
    return (
      <div className="app">
        <div className="bar"><button className="crumb" onClick={onBack}>‹ Настройки</button>
          <h1 className="t">Кто может входить</h1></div>
        <div className="scroll">
          <div className="skeleton" style={{ height: 84, marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 84 }} />
        </div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="app">
        <div className="bar"><button className="crumb" onClick={onBack}>‹ Настройки</button>
          <h1 className="t">Кто может входить</h1></div>
        <div className="scroll">
          <div className="blankslate">
            <b>Это может только владелец каталога</b>
            Попросите его добавить нужного человека.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Настройки</button>
        <h1 className="t">Кто может входить</h1>
        <p className="meta">
          Человек входит по своему номеру. Когда все его устройства вошли —
          закройте замок, и номер больше никого не пустит.
        </p>
      </div>

      <div className="scroll">
        {people.map((p) => {
          const locked = p.login_quota <= 0;
          const mine = p.id === meId;
          return (
            <div className="memo" key={p.id} style={{ marginTop: 0, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 17 }}>{p.name}</b>
                {mine && <span className="src src-assessed">вы</span>}
                {p.role === 'owner' && <span className="src src-certified">владелец</span>}
              </div>

              <div style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                {p.phone ? formatPhone(p.phone) : 'без номера'}
              </div>

              <div style={{ marginTop: 8, fontSize: 14 }}>
                {locked
                  ? <>Вход закрыт{p.devices > 0 && <> · {p.devices} {plural(p.devices, 'устройство', 'устройства', 'устройств')}</>}</>
                  : <>Открыто ещё {p.login_quota} {plural(p.login_quota, 'вход', 'входа', 'входов')}
                     {p.devices > 0 && <> · вошло {p.devices}</>}</>}
                {p.devices === 0 && !locked && <> · ждёт первого входа</>}
              </div>

              <div className="states" style={{ marginTop: 10 }}>
                {locked ? (
                  <button className="state-btn" disabled={Boolean(busy)}
                          onClick={() => void act(p.id, 'allow', { logins: 1 }, 'Вход открыт на одно устройство')}>
                    Открыть вход
                  </button>
                ) : (
                  <button className="state-btn" disabled={Boolean(busy)}
                          onClick={() => void act(p.id, 'lock', {}, 'Вход по номеру закрыт')}>
                    Закрыть вход
                  </button>
                )}
                {p.devices > 0 && (
                  <button className="state-btn" disabled={Boolean(busy)}
                          onClick={() => void act(p.id, 'revoke-devices', {},
                            mine ? 'Все устройства отключены — войдите заново' : 'Устройства отключены')}>
                    Отключить {p.devices === 1 ? 'устройство' : 'устройства'}
                  </button>
                )}
              </div>

              {mine && p.devices > 0 && (
                <p style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                  Отключение затронет и этот телефон — придётся войти заново.
                </p>
              )}
            </div>
          );
        })}

        {adding ? (
          <div className="memo" style={{ marginTop: 4 }}>
            <b style={{ display: 'block', marginBottom: 12 }}>Новый человек</b>
            <label className="field">
              <span>Имя</span>
              <input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setProblem(''); }}
                     placeholder="Как его называть" autoFocus />
            </label>
            <label className="field">
              <span>Номер телефона</span>
              <input value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setProblem(''); }}
                     placeholder="054-910-4882" inputMode="tel" />
            </label>
            <label className="field">
              <span>Сколько устройств</span>
              <div className="states" style={{ marginTop: 0 }}>
                {[1, 2, 3].map((n) => (
                  <button key={n} className="state-btn" aria-pressed={form.logins === n}
                          onClick={() => setForm({ ...form, logins: n })}>
                    {n}
                  </button>
                ))}
              </div>
              <span className="hint">
                Столько раз он сможет войти по номеру. Потом номер закроется сам.
              </span>
            </label>
            {problem && <p style={{ color: 'var(--alert)', fontSize: 14 }}>{problem}</p>}
            <div className="states">
              <button className="state-btn" onClick={() => { setAdding(false); setProblem(''); }}>Отмена</button>
              <button className="state-btn" aria-pressed disabled={busy === 'new'} onClick={() => void add()}>
                {busy === 'new' ? 'Добавляю…' : 'Добавить'}
              </button>
            </div>
          </div>
        ) : (
          <button className="btn btn-secondary" style={{ marginTop: 4 }} onClick={() => setAdding(true)}>
            ＋ Добавить человека
          </button>
        )}

        <div className="memo">
          <b>Почему кнопок две.</b> Замок запрещает новые входы по номеру, но
          устройства, вошедшие раньше, продолжают работать. Если телефон
          потерян — отключите устройства, иначе замок ничего не решает.
        </div>
      </div>

      {toast && <div className="pill">{toast}</div>}
    </div>
  );
}
